import React, { FC, useState } from 'react';

import cn from 'classnames';
import { Control, Controller } from 'react-hook-form';
import { InputActionMeta } from 'react-select';
import StateManagedSelect, { default as SelectComponent } from 'react-select/creatable';
import { Tooltip } from 'react-tooltip';
import { twMerge } from 'tailwind-merge';

import useModal from 'hooks/use-modal/useModal';
import { FormItem, FromItemHigligtedColor, Option } from 'interfaces';
import HighlightedMessage from 'shared-components/higlighted-message/HighlightedMessage';

import { ErrorMessage, FormLabel } from './index';

export type Props = {
  control: Control;
  isMulti?: boolean;
  className?: string;
  labelClassName?: string;
  isSearchable?: boolean;
  anchorPrefix?: string;
  isAbsoluteError?: boolean;
  isClearable?: boolean;
  onClearCallback?: VoidFunction;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  creatableOff?: boolean;
  closeMenu?: boolean;
  selectAll?: boolean;
  isLoading?: boolean;
  fetchAllCallback?: () => Promise<Option[]>;
} & Partial<StateManagedSelect> &
  Omit<FormItem, 'component'>;
const CreatableSelectFormInput: FC<Props> = ({
  isMulti,
  className,
  label,
  name,
  options,
  control,
  placeholder,
  disabled,
  validation,
  labelClassName,
  customCompanySelectStyles,
  isSearchable = true,
  tooltipContent,
  anchorPrefix,
  highlighted,
  highlightedDescription,
  isAbsoluteError,
  isClearable,
  onClearCallback,
  onCreateOption,
  highlightedColor = FromItemHigligtedColor.Blue,
  onInputChange,
  creatableOff,
  closeMenu,
  selectAll,
  isLoading,
  fetchAllCallback,
}) => {
  const { isOpen: isTooltipOpen, onOpen: handleOpenTooltip, onClose: handleCloseTooltip } = useModal(false);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const anchorId = `${anchorPrefix}-${name}`;

  const renderedTooltip = (): React.ReactElement | string => {
    if (!tooltipContent) return '';

    if (typeof tooltipContent === 'function') {
      const Component = tooltipContent;
      return <Component />;
    }

    return tooltipContent;
  };

  return (
    <div
      className={twMerge('mb-6 w-full', className)}
      id={anchorId}
      onMouseEnter={handleOpenTooltip}
      onMouseLeave={handleCloseTooltip}
    >
      <>
        {typeof label === 'string' ? <FormLabel className={labelClassName}>{label}</FormLabel> : label}
        <Controller
          control={control}
          name={name}
          rules={validation}
          render={({ field: { onChange, ...field }, fieldState: { error } }) => {
            const selectAllOption = { value: '__select_all__', label: 'Select All' };
            const mergedOptions = isLoading
              ? options || []
              : isMulti
              ? [selectAllOption, ...(options || [])]
              : options || [];

            return (
              <>
                <SelectComponent
                  closeMenuOnSelect={closeMenu ? false : true}
                  isSearchable={isSearchable}
                  options={selectAll ? mergedOptions : options}
                  menuIsOpen={menuIsOpen}
                  onMenuOpen={() => setMenuIsOpen(true)}
                  onMenuClose={() => setMenuIsOpen(false)}
                  onChange={(selectedOption, triggeredAction) => {
                    if (isMulti && Array.isArray(selectedOption)) {
                      const isSelectAllChosen = selectedOption.some((option) => option.value === '__select_all__');
                      if (isSelectAllChosen) {
                        setMenuIsOpen(false); // Close menu after selecting all.
                        if (fetchAllCallback) {
                          fetchAllCallback().then((allOptions) => {
                            onChange(allOptions);
                          });
                        } else {
                          // Fall back to the current options (which might be paginated) if no callback is provided.
                          onChange(options);
                        }
                        return;
                      }
                    }
                    if (triggeredAction.action === 'clear' && onClearCallback) {
                      onClearCallback();
                    }
                    onChange(selectedOption);
                  }}
                  onInputChange={(inputValue, actionMeta) => {
                    if (onInputChange) {
                      onInputChange(inputValue, actionMeta);
                    }
                  }}
                  isMulti={isMulti}
                  placeholder={isLoading ? 'Loading...' : placeholder}
                  isLoading={isLoading}
                  noOptionsMessage={() => (isLoading ? 'Loading...' : 'No options')}
                  isDisabled={disabled}
                  isClearable={isClearable}
                  isValidNewOption={creatableOff ? () => false : onCreateOption}
                  menuPosition='fixed'
                  menuPortalTarget={window.document.body}
                  styles={{
                    dropdownIndicator: (styles) => ({
                      ...styles,
                      color: '#4068BF',
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    ...customCompanySelectStyles,
                  }}
                  className={cn('select-input div-py-0', {
                    'select-input-error': error,
                    [`select-input-highlighted-${highlightedColor}`]: !error && highlighted,
                    '[&>div]:!bg-grey-100 [&>div]:!border-grey-200 [&_div]:!text-grey-300': disabled,
                  })}
                  {...field}
                  value={
                    // if the field value is null or contains a null value, set to null for react-select.
                    Object.values(field?.value || {}).includes(null) ? null : field.value
                  }
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                />

                <ErrorMessage error={error?.message} isAbsoluteError={isAbsoluteError} />

                {!error && highlightedDescription && (
                  <HighlightedMessage className='input-error-message' color={highlightedColor}>
                    {highlightedDescription}
                  </HighlightedMessage>
                )}
              </>
            );
          }}
        />
      </>
      {tooltipContent && isTooltipOpen && (
        <Tooltip
          anchorId={anchorId}
          offset={-5}
          positionStrategy='fixed'
          className='text-sm text-grey-500'
          isOpen
          clickable
          noArrow
        >
          {renderedTooltip()}
        </Tooltip>
      )}
    </div>
  );
};

export default CreatableSelectFormInput;
